import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { pagesApi } from "../services/api";

function Navigation() {
  const location = useLocation();
  const [navItems, setNavItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const fetchNavigation = async () => {
      try {
        const response = await pagesApi.getAll(false); // Only active pages
        const pages = response.data || [];

        // Map pages to navigation items with custom ordering
        const navMapping = {
          home: { label: "Home", path: "/", order: 1 },
          consulting: { label: "Consulting", path: "/consulting", order: 2 },
          coaching: { label: "Coaching", path: "/coaching", order: 3 },
          "corporate-training": {
            label: "Corporate Training",
            path: "/corporate-training",
            order: 4,
          },
          programs: { label: "Programs", path: "/programs", order: 5 },
          insights: { label: "Insights", path: "/insights", order: 6 },
          about: { label: "About", path: "/about", order: 7 },
          contact: { label: "Contact", path: "/contact", order: 8 },
        };

        const items = pages
          .map((page) => {
            const pageType = page.page_type || page.slug;
            const mapping = navMapping[pageType];
            if (mapping) {
              return {
                ...mapping,
                title: page.title,
                label: page.title,
              };
            }
            return null;
          })
          .filter(Boolean)
          .sort((a, b) => a.order - b.order);

        setNavItems(items);
      } catch (error) {
        console.error("Failed to fetch navigation:", error);
        // Fallback to default navigation if API fails
        setNavItems([
          { label: "Home", path: "/", order: 1 },
          { label: "Programs", path: "/programs", order: 2 },
          {
            label: "Corporate Training",
            path: "/corporate-training",
            order: 3,
          },
          { label: "Consulting", path: "/consulting", order: 4 },
          { label: "Coaching", path: "/coaching", order: 5 },
          { label: "Insights", path: "/insights", order: 6 },
          { label: "About", path: "/about", order: 7 },
          { label: "Contact", path: "/contact", order: 8 },
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchNavigation();
  }, []);

  const isActive = (path) => {
    return location.pathname === path
      ? "text-[#3B82F6] font-semibold"
      : "text-[#111827]";
  };

  return (
    <nav className="bg-white shadow-lg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <Link to="/" className="text-2xl font-semibold text-[#1F3A5F]">
              Innobig
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {loading ? (
              <div className="text-gray-500">Loading...</div>
            ) : (
              navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`hover:text-[#3B82F6] transition-colors ${isActive(item.path)}`}
                >
                  {item.label}
                </Link>
              ))
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-[#111827] hover:text-[#3B82F6]"
            >
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden pb-4 border-t border-gray-200">
            {loading ? (
              <div className="text-gray-500 px-4 py-2">Loading...</div>
            ) : (
              navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block px-4 py-2 hover:bg-gray-100 hover:text-[#3B82F6] transition-colors ${isActive(item.path)}`}
                >
                  {item.label}
                </Link>
              ))
            )}
          </div>
        )}
      </div>
    </nav>
  );
}

export default Navigation;
